'use client';

import React from 'react';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'readOnly'> {
  label?: string;
  error?: string;
  fullWidth?: boolean;
  /** dark = fundo escuro (auth), light = fundo claro (perfil, formulários) */
  variant?: 'dark' | 'light';
  /**
   * Somente leitura: valor visível, sem edição; aparência de campo que não aceita edição.
   *
   * - No HTML, `readOnly` ainda participa do submit; `disabled` **não** entra no `FormData`.
   *   Se `readOnly` for `true`, qualquer `disabled` passado é **ignorado** no DOM (com aviso em DEV).
   * - Valor que chega depois do primeiro render (Auth, API): use `value` **controlado**; `defaultValue`
   *   sozinho não atualiza o DOM quando a prop muda.
   */
  readOnly?: boolean;
}

const labelClassByVariant = {
  dark: 'text-white/90',
  light: 'text-navy-dark',
};

const inputClassByVariant = {
  dark: `
    text-white placeholder-white/40
    bg-white/10 border border-white/20
  `.trim(),
  light: `
    text-text-body placeholder-text-muted
    bg-[#fafbfc] border border-border-light
  `.trim(),
};

const errorClassByVariant = {
  dark: 'text-red-300',
  light: 'text-red-600',
};

/** Fundo/borda/texto alinhados a “não editável”, sem `disabled`. */
const readOnlyInputClassByVariant = {
  dark: `
    cursor-default select-text
    bg-white/[0.06] border-white/15 text-white/90 placeholder-white/30
    focus:border-white/20 focus:ring-0
  `.trim(),
  light: `
    cursor-default select-text
    bg-[#eef1f6] border-[#dde3ec] text-text-muted placeholder-text-muted/70
    focus:border-border-light focus:ring-0
  `.trim(),
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      fullWidth = false,
      variant = 'dark',
      className = '',
      id: idProp,
      readOnly = false,
      disabled: disabledProp,
      ...props
    },
    ref
  ) => {
    const reactId = React.useId();
    const id = idProp ?? `input-${reactId.replace(/:/g, '')}`;
    const widthClass = fullWidth ? 'w-full' : '';

    const disabledOnDom = readOnly ? false : !!disabledProp;
    if (import.meta.env.DEV && readOnly && disabledProp) {
      // eslint-disable-next-line no-console -- contrato de formulário / submit
      console.warn(
        '[Input] `readOnly` está ativo: `disabled` foi ignorado no DOM. ' +
          'Campos `disabled` não entram no submit (FormData). Use só `readOnly` para bloquear edição mantendo o valor.'
      );
    }

    return (
      <div className={`${widthClass}`}>
        {label && (
          <label
            htmlFor={id}
            className={`block text-sm font-medium mb-1 ${labelClassByVariant[variant]}`}
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={id}
          readOnly={readOnly}
          className={`
            w-full rounded-lg px-3 py-2.5 text-sm min-h-[40px]
            ${readOnly ? readOnlyInputClassByVariant[variant] : inputClassByVariant[variant]}
            focus:outline-none
            ${readOnly ? '' : 'focus:border-cyan focus:ring-1 focus:ring-cyan'}
            transition-colors duration-200
            disabled:opacity-50 disabled:cursor-not-allowed
            ${error ? 'border-red-400 focus:border-red-400 focus:ring-red-400' : ''}
            ${className}
          `.trim()}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          {...props}
          disabled={disabledOnDom}
        />
        {error && (
          <p
            id={`${id}-error`}
            className={`mt-1 text-sm ${errorClassByVariant[variant]}`}
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
